import { getInitialGridStylesAndSizes, applyDelta } from './panel-sizes.js';
import { getPanelIndex, getPanelByName, getNextPanelIndex } from './panels.js';
import {
  pxToFractions,
  fractionsToTargetUnit,
  anyUnitToFractions,
  ParsedValue,
} from './units.js';
import { RESIZE_HANDLE_SIZE } from '../Resizer.js';
import {
  ResizeHandles,
  PanelChildren,
  PanelSizesWithUnit,
} from '../types/shared-panel-types.js';

/**
 * Dismiss a panel by name.
 * Adjust the sizes of the remaining panels accordingly.
 * @returns Array with new sizes, sizes to start animation with, and sizes to animate to.
 */
export function dismissPanel(
  panelName: string,
  sizes: PanelSizesWithUnit,
  sizesStart: PanelSizesWithUnit,
  sizesEnd: PanelSizesWithUnit,
  sortedPanels: PanelChildren,
  sortedResizeHandles: ResizeHandles,
  rootWidth: number,
) {
  const currentPanel = getPanelByName(sortedPanels, panelName);

  // We can only re-distribute a dismissed panel's size if it actually exists
  // in the sizes object. It may still be empty.
  if (!currentPanel || !sizes[panelName]) {
    return [];
  }

  const dismissedPanelFractions =
    anyUnitToFractions(
      sizes[panelName],
      rootWidth,
      // The resize handle of the dismissed panel is already gone. To get the correct
      // fractions we have to still include it, though, which is why we increase by 1 here.
      sortedResizeHandles.length + 1,
    ) || 0;
  const resizablePanels = sortedPanels.filter(
    (panel) => panel.resizable === true,
  ).length;

  const deltaX =
    dismissedPanelFractions + pxToFractions(RESIZE_HANDLE_SIZE, rootWidth, 0);
  const panelIndex = getPanelIndex(panelName, sortedPanels);
  const direction = currentPanel.dismissible === 'left' ? 1 : -1;
  const nextPanel = getNextPanelIndex(sortedPanels, panelIndex, {
    direction: currentPanel.dismissible === 'left' ? 1 : -1,
    loop: true,
    skipNonResizable: resizablePanels >= 2,
  });

  const newSizes = applyDelta(
    sizes,
    sortedPanels,
    sortedResizeHandles,
    deltaX,
    rootWidth,
    nextPanel,
    direction,
    true,
    'dismissed',
  );

  if (newSizes) {
    delete newSizes[panelName];
    const adjustedSizes = adjustSizesToAvailableSpace(
      newSizes,
      rootWidth,
      sortedResizeHandles.length,
      'decrease',
    );

    const animateSizesStart = { ...sizesStart };
    const animateSizesEnd = {
      ...sizesEnd,
      ...adjustedSizes,
      [panelName]: { value: 0, unit: 'fr' } as ParsedValue,
    };

    return [adjustedSizes, animateSizesStart, animateSizesEnd];
  }

  return [];
}

/**
 * Add a panel by name.
 * Adjust the sizes of existing panels to make space for the new one.
 * @returns Array with new sizes, sizes to start animation with, and sizes to animate to.
 */
export function addPanel(
  panelName: string,
  sizes: PanelSizesWithUnit,
  sizesStart: PanelSizesWithUnit,
  sizesEnd: PanelSizesWithUnit,
  restoreSizes: PanelSizesWithUnit,
  sortedPanels: PanelChildren,
  sortedResizeHandles: ResizeHandles,
  rootWidth: number,
) {
  let deltaX = 0;
  const resizeHandleFr = pxToFractions(RESIZE_HANDLE_SIZE, rootWidth, 0);

  let addedPanelTargetWidth: ParsedValue;
  const addedPanel = getPanelByName(sortedPanels, panelName);

  if (!addedPanel) {
    // just for typescript
    return [sizes];
  }

  const panelIndex = getPanelIndex(panelName, sortedPanels);
  const direction = addedPanel.dismissible === 'left' ? 1 : -1;

  // If the panel was rendered before, we need to shrink the existing panels by the prev panel size.
  if (restoreSizes[panelName]) {
    addedPanelTargetWidth = restoreSizes[panelName];
    // If the panel was not yet rendered, we get the width it would take up when
    // initializing the page and shrink existing panels by that.
  } else {
    const { initialPanelSizes } = getInitialGridStylesAndSizes(
      rootWidth,
      sortedPanels,
      sortedResizeHandles,
    );
    addedPanelTargetWidth = initialPanelSizes[panelName];
  }

  // The target width may be any unit, we need to convert it to fractions before resizing the other panels.
  const addedPanelTargetFractions =
    anyUnitToFractions(
      addedPanelTargetWidth,
      rootWidth,
      sortedResizeHandles.length,
    ) || 0;

  // The resize handle needs to be added to the applied delta to make enough space for
  // the new panel + its corresponding resize handle. The added panel itself must not
  // include the resizeHandle. This will be subtracted farther down again.
  deltaX = addedPanelTargetFractions + resizeHandleFr;

  const newSizes = applyDelta(
    {
      ...adjustSizesToAvailableSpace(
        sizes,
        rootWidth,
        sortedResizeHandles.length,
      ),
      [panelName]: { value: 0, unit: 'fr' },
    },
    sortedPanels,
    sortedResizeHandles,
    deltaX,
    rootWidth,
    panelIndex,
    direction,
    // TODO: going left, if the panels to the left of the added panel can't shrink enough, we should first try and
    //       shrink the added panel to make it fit before shrinking panels to the right of the panel. Not relevant for the page.
    //       follow-up: ADES-3362
    true,
    'added',
  );

  if (newSizes) {
    // Subtract the resize handle again from the added panel's width.
    newSizes[panelName].value -= resizeHandleFr;

    // If the panel is not resizable, we want to set the template column to use
    // the unit we got from the panel's initial size / restore size instead of fractions.
    if (!addedPanel.resizable) {
      const value =
        fractionsToTargetUnit(
          addedPanelTargetFractions,
          addedPanelTargetWidth.unit,
          rootWidth,
          sortedResizeHandles.length,
        ) || 0;
      newSizes[panelName] = { value, unit: addedPanelTargetWidth.unit };
    }

    const animateSizesStart = {
      ...sizes,
      ...sizesStart,
      [panelName]: { value: 0, unit: 'fr' } as ParsedValue,
    };
    const animateSizesEnd = { ...sizesEnd, ...newSizes };

    return [newSizes, animateSizesStart, animateSizesEnd];
  }

  return [];
}

/**
 * Multiply the current sizes with the percentage difference of the available size
 * before and after adding a panel to make sure we adjust the sizes by the space
 * that is now taken up / freed up by the additional / missing resize handle.
 *
 * This will prevent resize handles from jumping when conditionally rendering panels.
 */
export function adjustSizesToAvailableSpace(
  sizes: PanelSizesWithUnit,
  rootWidth: number,
  resizeHandlesCount: number,
  panelCount: 'increase' | 'decrease' = 'increase',
): PanelSizesWithUnit {
  const resizeHandlesCountOffset =
    (rootWidth -
      (resizeHandlesCount + (panelCount === 'increase' ? -1 : 1)) *
        RESIZE_HANDLE_SIZE) /
    (rootWidth - resizeHandlesCount * RESIZE_HANDLE_SIZE);

  return Object.entries(sizes).reduce<PanelSizesWithUnit>(
    (acc, [key, parsedValue]) => {
      acc[key] = {
        value:
          Math.round(parsedValue.value * resizeHandlesCountOffset * 100) / 100,
        unit: parsedValue.unit,
      };
      return acc;
    },
    {},
  );
}
